import { ReusableFunctions } from '../classes/reusable-functions';
import { CategoryGroup } from '../interfaces/card-data';
import { NavigationHelpService } from 'app/modules/shared/services/navigation-help.service';

export class PipelineChartOptions {
    constructor(
        private reusableFunctions: ReusableFunctions,
        private navigationHelpService: NavigationHelpService
    ) {}

    loadChartOptions(
        series_1_data,
        series_2_data,
        series_1_title,
        series_2_title,
        clickablePoints: boolean = false,
        xTitle,
        url: string,
        compareTo: string,
        categoryGroup: CategoryGroup[] | string[]
    ): any {
        let maxValDataSeries = Math.max(
            this.reusableFunctions.getMaxValue(series_1_data, 'y'),
            this.reusableFunctions.getMaxValue(series_2_data, 'y')
        );
        let highChartsOptions: Highcharts.Options;
        let showMarker = false;
        let yTitle = 'Volume';
        if (series_2_data.length === 1) {
            showMarker = true;
        }

        const _this = this;

        highChartsOptions = {
            chart: {
                spacingBottom: 0,
                spacingLeft: 0,
                marginRight: 0,
                spacingRight: 0,
                spacingTop: 5,
                height: 330,
            },
            exporting: {
                enabled: false,
            },
            title: {
                text: '',
            },
            credits: {
                enabled: false,
            },
            series: [
                {
                    name: series_1_title,
                    type: 'column',
                    point: {
                        events: {
                            click: function () {
                                if (clickablePoints) {
                                    let queryParams = {
                                        selectedPeriod: this.properties.period,
                                        compareTo: compareTo,
                                        periodGroup: 'T',
                                        titleSuffix: '',
                                    };
                                    _this.navigationHelpService.navigateTo(
                                        [url],
                                        queryParams,
                                        true
                                    );
                                }
                            },
                        },
                    },
                    groupPadding: 0,
                    //borderRadiusTopLeft: 6,
                    //borderRadiusTopRight: 6,
                    data: series_1_data,
                    cursor: clickablePoints ? 'pointer' : 'default',
                } as Highcharts.SeriesColumnOptions,

                {
                    name: series_2_title,
                    type: 'spline',
                    point: {
                        events: {
                            click: function () {
                                if (clickablePoints) {
                                    let queryParams = {
                                        selectedPeriod: this.properties.period,
                                        compareTo: compareTo,
                                        periodGroup: 'C',
                                        titleSuffix: ': ' + this.series.name,
                                        showOnlyPreviusDateRange: compareTo.includes('Previous'),
                                    };
                                    _this.navigationHelpService.navigateTo(
                                        [url],
                                        queryParams,
                                        true
                                    );
                                }
                            },
                        },
                    },
                    data: series_2_data,
                    color: '#A6266E',
                    cursor: clickablePoints ? 'pointer' : 'default',
                } as Highcharts.SeriesSplineOptions,
            ],
            xAxis: [
                {
                    type: 'category',
                    categories: categoryGroup,
                    tickLength: 1,
                    tickInterval: 1,
                    pointInterval: 1,
                    labels: {
                        enabled: true,
                        style: {
                            fontSize: '10px',
                            color: '#666666',
                        },
                    },
                    title: {
                        text: xTitle,
                        style: {
                            fontSize: '10px',
                            color: '#666666',
                        },
                    },
                } as Highcharts.XAxisOptions,
            ],

            tooltip: {
                formatter: function () {
                    let length = this.points.length;
                    let thisPeriodTotal = 0;
                    let thisPeriodUnitTotal = 0;
                    let comparePeriodTotal = 0;
                    let comparePeriodUnitTotal = 0;
                    let secondarytitle = this.points[0].series.name.toUpperCase();
                    let tooltip =
                        '<table>' +
                        this.points.reduce(function (s, point, index) {
                            let isPreviousPeriod =
                                point.series.name.toLowerCase() === series_2_title.toLowerCase();
                            if (isPreviousPeriod) {
                                comparePeriodTotal += point.y;
                                comparePeriodUnitTotal += point.point.properties.units;
                            } else {
                                thisPeriodUnitTotal += point.point.properties.units;
                                thisPeriodTotal += point.y;
                            }
                            s +=
                                '<tr><td style="color:' +
                                point.point.color +
                                '; padding-right: 10px;"><b>' +
                                point.point.properties.datePeriodDescription.toUpperCase() +
                                '</b></td><td style="text-align: right;">' +
                                _this.reusableFunctions.formatMoney(point.y) +
                                '</td><td style="padding: 0 5px 0 5px">/</td>' +
                                '<td style="text-align: right;">' +
                                point.point.properties.units.toLocaleString() +
                                '</td></tr>';

                            if (index === length - 1) {
                                let percentVoumeInfo = _this.reusableFunctions.getPercentInfo(
                                    thisPeriodTotal,
                                    comparePeriodTotal,
                                    false
                                );
                                let percentUnitInfo = _this.reusableFunctions.getPercentInfo(
                                    thisPeriodUnitTotal,
                                    comparePeriodUnitTotal,
                                    false
                                );

                                s +=
                                    '<tr style="line-height: 16px;"><td></td><td style="text-align: right; color:' +
                                    percentVoumeInfo.percentColor +
                                    ';"><b><span><i style="padding-right: 5px"class="' +
                                    percentVoumeInfo.caret +
                                    '"></i>' +
                                    percentVoumeInfo.percentValue +
                                    '</b></span></td>' +
                                    '<td style="padding: 0 5px 0 5px">/</td><td style="text-align: right; color:' +
                                    percentUnitInfo.percentColor +
                                    ';"><b><span><i style="padding-right: 5px" class="' +
                                    percentUnitInfo.caret +
                                    '"></i>' +
                                    percentUnitInfo.percentValue +
                                    '</b></span></td></tr>';
                            }

                            return s;
                        }, '<tr style="line-height: 18px; font-size: 16px"><td colSpan="4"><b>' +
                            secondarytitle +
                            '<b></td></tr>') +
                        '</table>';

                    return tooltip;
                },
                shared: true,
                useHTML: true,
                style: {
                    color: '#223540',
                    fontFamily: 'Roboto',
                    fontSize: '12',
                    height: 12,
                },
                borderColor: '#223540',
            } as Highcharts.TooltipOptions,

            yAxis: [
                {
                    title: {
                        text: yTitle + ' ($)',
                        style: {
                            fontSize: '10px',
                            color: '#666666',
                        },
                    },

                    max: maxValDataSeries,
                    labels: {
                        enabled: true,
                        style: {
                            fontSize: '10px',
                            color: '#666666',
                        },
                    },
                    lineWidth: 1,
                    units: {},
                } as Highcharts.YAxisOptions,
            ],

            legend: {
                enabled: false,
            },
            plotOptions: {
                column: {
                    states: {
                        hover: {
                            brightness: -0.3,
                        },
                    },
                },
                series: {
                    marker: {
                        enabled: showMarker,
                        states: {
                            hover: {
                                radius: 4,
                                enabled: true,
                                radiusPlus: 0,
                                lineWidthPlus: 0,
                                lineWidth: 0,
                            },
                        },
                    },
                    maxPointWidth: 50,
                    groupPadding: 0,
                    lineWidth: 3,
                    gridLineWidth: 0,
                    minorGridLineWidth: 0,
                } as Highcharts.PlotSeriesOptions,
            },
        };
        return highChartsOptions;
    }

    public createOperationsAssignmentsNegativeStackChart(stackChartData): Highcharts.Options {
        const { title, categories, largestNumber, loanData } = stackChartData;
        let _this = this;

        return {
            chart: {
                type: 'bar',
                events: {
                    load() {
                        this.yAxis[0].setExtremes(largestNumber * -1, largestNumber);
                    },
                    render: function () {
                        var xAxis = this.xAxis[0];
                        var xTicks = xAxis.ticks;
                        let tickKeys = Object.keys(xTicks);
                        let points: Highcharts.Point[] = [];
                        this.series.forEach((series) => {
                            points = points.concat(series.points);
                        });
                        try {
                            tickKeys.forEach((key, i) => {
                                let xTick = xTicks[key];
                                if (xTick && xTick.label) {
                                    var label = xTick.label.element;
                                    label.style.cursor = 'pointer';
                                    label.onclick = function (item) {
                                        const point = points[xTick.pos];
                                        let queryParams = {
                                            staffingKey: point.properties.staffingKey,
                                            showAssigned: 1,
                                            showUnassigned: 1,
                                            daysToExpiration: 999,
                                            milestone: point.properties.milestone,
                                            titleSuffix: `Loans in Milestone: ${point.properties.milestone}`,
                                        };
                                        _this.navigationHelpService.navigateTo(
                                            ['reporting/pipeline/ops-status-details'],
                                            queryParams,
                                            true
                                        );
                                    };
                                }
                            });
                        } catch (error) {
                            throw error;
                        }
                    },
                },
            },
            exporting: {
                enabled: false,
            },
            title: {
                text: title,
                align: 'left',
            },
            subtitle: {
                text: '',
                align: 'left',
            },
            accessibility: {
                point: {
                    valueDescriptionFormat: '{index}. Age {xDescription}, {value}%.',
                },
            },
            xAxis: [
                {
                    categories: categories,
                    reversed: true,
                    labels: {
                        step: 1,
                    },
                },
                {
                    opposite: true,
                    reversed: true,
                    categories: categories,
                    linkedTo: 0,
                    labels: {
                        step: 1,
                        enabled: false,
                    },
                },
            ],
            yAxis: {
                title: {
                    text: null,
                },
                labels: {
                    formatter: function () {
                        return Math.abs(Number(this.value)).toString();
                    },
                },
            },
            plotOptions: {
                series: {
                    stacking: 'normal',
                    point: {
                        events: {
                            click: function (e) {
                                const isAssignedTitle = this.properties.assigned
                                    ? 'Assigned'
                                    : 'Unassigned';
                                let queryParams = {
                                    staffingKey: this.properties.staffingKey,
                                    showAssigned: this.properties.assigned ? 1 : 0,
                                    showUnassigned: this.properties.assigned ? 0 : 1,
                                    daysToExpiration: 999,
                                    milestone: this.properties.milestone,
                                    titleSuffix: `${isAssignedTitle} Loans by Position: ${this.properties.position}, ${this.properties.milestone}`,
                                };
                                _this.navigationHelpService.navigateTo(
                                    ['reporting/pipeline/ops-status-details'],
                                    queryParams,
                                    true
                                );
                            },
                        },
                    },
                    cursor: 'pointer',
                } as Highcharts.PlotSeriesOptions,
            },
            tooltip: {
                formatter: function () {
                    return '<b>' + this.series.name + ':</b> ' + Math.abs(this.point.y) + ' loans';
                },
            },

            series: [
                {
                    name: 'Assigned',
                    type: undefined,
                    data: loanData.assignedLoans,
                },
                {
                    name: 'Unassigned',
                    type: undefined,
                    data: loanData.unassignedLoans,
                },
            ],
            credits: {
                enabled: false,
            },
        };
    }

    public createOperationsAssignmentsBasicBar(barData): Highcharts.Options {
        const { title, xAxisCategories, scrollAmount, seriesData } = barData;

        const _this = this;

        return {
            chart: {
                type: 'bar',
                scrollablePlotArea: {
                    minHeight: scrollAmount,
                    scrollPositionX: 0,
                    opacity: 1,
                },
                marginRight: 30,
                events: {
                    render: function () {
                        var xAxis = this.xAxis[0];
                        var xTicks = xAxis.ticks;
                        let tickKeys = Object.keys(xTicks);
                        let points: Highcharts.Point[] = [];
                        this.series.forEach((series) => {
                            points = points.concat(series.points);
                        });
                        try {
                            tickKeys.forEach((key, i) => {
                                let xTick = xTicks[key];
                                if (xTick && xTick.label) {
                                    var label = xTick.label.element;
                                    label.style.cursor = 'pointer';
                                    label.onclick = function (item) {
                                        const point = points[xTick.pos];
                                        let queryParams = {
                                            staffingKey: point.properties.staffingKey,
                                            showAssigned: 1,
                                            showUnassigned: 0,
                                            daysToExpiration: 999,
                                            milestone: 'All',
                                            employeeIdentifier: point.properties.employeeIdentifier,
                                            titleSuffix: `${point.properties.position} Assignments: ${point.properties.name}`,
                                        };
                                        _this.navigationHelpService.navigateTo(
                                            ['reporting/pipeline/ops-assignments-details'],
                                            queryParams,
                                            true
                                        );
                                    };
                                }
                            });
                        } catch (error) {
                            throw error;
                        }
                    },
                },
            },
            exporting: {
                enabled: false,
            },
            title: {
                text: title,
                align: 'left',
            },
            subtitle: {
                text: '',
                align: 'left',
            },
            xAxis: {
                categories: xAxisCategories,
                title: {
                    text: null,
                },
                labels: {
                    step: 1,
                },
            },
            yAxis: {
                allowDecimals: false,
                title: {
                    text: '# of Loans',
                    align: 'high',
                },
                labels: {
                    overflow: 'justify',
                },
            },
            tooltip: {
                style: {
                    fontFamily: 'Roboto',
                    fontSize: '12',
                    height: 12,
                },
                borderColor: 'white',
                backgroundColor: 'white',
                borderRadius: 7,
                borderWidth: 1,
                useHTML: true,
                formatter: function () {
                    const colorObject = {
                        Conv: 'rgb(184, 112, 255)',
                        'Non-Conf': 'rgb(255, 219, 112)',
                        Reverse: 'rgb(44, 118, 162)',
                        Govt: 'rgb(87, 215, 133)',
                        Other: 'rgb(255, 148, 148)',
                    };
                    let property = '';
                    for (let pointProp of this.point.properties.associatedData) {
                        const color = `color: ${colorObject[pointProp.loanTypeCategory]}`;
                        const loans = pointProp.assignedLoans > 1 ? 'loans' : 'loan';

                        property += `<div class="operations-assignments-tooltip-data"><div class="operations-assignments-tooltip-icon" style='${color}'>•</div>
                        <div style="padding-bottom: 2px;"><span style="display: inline-block; width: 55px">${pointProp.loanTypeCategory}:</span> <span> ${pointProp.assignedLoans} ${loans}</span></div></div>`;
                    }
                    const title = `<div class="operations-assignments-container" style="margin-bottom: 7px; font-weight: bold">
                                    <div class="operations-assignments-title">
                                        <div class="operations-assignments-title-sub">${this.series.name}</div>
                                    </div>
                                </div>`;
                    return `${title} 
                            <div class="operations-assignments-tooltip-data-container">
                                ${property}
                            </div>

                    </div>`;
                },
            },
            plotOptions: {
                bar: {
                    dataLabels: {
                        enabled: true,
                    },
                    pointWidth: 8,
                },
                series: {
                    point: {
                        events: {
                            click: function (e) {
                                let queryParams = {
                                    staffingKey: this.properties.staffingKey,
                                    showAssigned: 1,
                                    showUnassigned: 0,
                                    daysToExpiration: 999,
                                    milestone: this.properties.milestone,
                                    employeeIdentifier: this.properties.employeeIdentifier,
                                    titleSuffix: `${this.properties.position} Assignments @ ${this.properties.milestone}: ${this.properties.name}`,
                                };
                                _this.navigationHelpService.navigateTo(
                                    ['reporting/pipeline/ops-assignments-details'],
                                    queryParams,
                                    true
                                );
                            },
                        },
                    },
                    dataLabels: {
                        enabled: true,
                        formatter: function () {
                            if (this.y > 0) {
                                return this.y;
                            }
                        },
                    },
                    cursor: 'pointer',
                } as Highcharts.PlotSeriesOptions,
            },
            legend: {
                enabled: true,
                align: 'left',
                verticalAlign: 'top',
                itemStyle: {
                    fontSize: '12px',
                    fontWeight: 'normal',
                },
                symbolHeight: 10,
                y: -15,
                x: -10,
            },
            credits: {
                enabled: false,
            },
            series: seriesData,
        };
    }

    public createOperationsPipelineBasicBars(barData): Highcharts.Options {
        const { title, xAxisCategories, seriesData, pointWidth } = barData;
        const _this = this;

        return {
            chart: {
                type: 'bar',
                scrollablePlotArea: {
                    scrollPositionX: 0,
                    opacity: 1,
                },
                marginRight: 30,
            },
            exporting: {
                enabled: false,
            },
            title: {
                text: title,
                align: 'left',
            },
            subtitle: {
                text: '',
                align: 'left',
            },
            xAxis: {
                categories: xAxisCategories,
                title: {
                    text: null,
                },
            },
            yAxis: {
                allowDecimals: false,
                title: {
                    text: '# of Loans',
                    align: 'high',
                },
                labels: {
                    overflow: 'justify',
                },
            },
            tooltip: {
                style: {
                    color: '#223540',
                    fontFamily: 'Roboto',
                    fontSize: '20',
                    height: 12,
                },
                borderColor: 'white',
                borderRadius: 6,
                backgroundColor: '#FFFFFF',
                useHTML: true,
                followPointer: true,
                distance: 15,
                formatter: function () {
                    const tooltipValue = `Loans: ${this.y}`;
                    return `<div class="operations-pipeline-container">
                        <div class="operations-pipeline-tooltip-title">
                            <div class="operations-pipeline-tooltip-name">${this.point.properties.title}</div>
                        </div>
                        <div class="operations-pipeline-tooltip-title operations-pipeline-tooltip-value" style="border-bottom: none">
                            <div class="operations-pipeline-tooltip-icon" style="color: ${this.point.color}">•</div>
                            <div style="font-size: 12px;">${tooltipValue}</div>
                        </div>
                    </div>`;
                },
            },
            plotOptions: {
                bar: {
                    dataLabels: {
                        enabled: true,
                    },
                    pointWidth,
                },
                series: {
                    point: {
                        events: {
                            click: function (e) {
                                let queryParams: any = {
                                    milestone: 'All',
                                    lockStatus: 'All',
                                };
                                if (this.properties.disclosureStage)
                                    queryParams.disclosureStage = this.properties.disclosureStage;
                                if (this.properties.expirationDays) {
                                    queryParams.expirationDays = this.properties.expirationDays;
                                    queryParams.lockStatus = 'L';
                                }

                                _this.navigationHelpService.navigateTo(
                                    [this.properties.reportURL],
                                    queryParams,
                                    true
                                );
                            },
                        },
                    },
                    cursor: 'pointer',
                } as Highcharts.PlotSeriesOptions,
            },
            legend: {
                enabled: false,
            },
            credits: {
                enabled: false,
            },
            series: seriesData,
        };
    }
}
